// src/components/Inbox.jsx
import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { useAuthContext } from "../context";
import { conversationsURL } from "../api/api";
import ChatContent from "./ChatContent";
import { Helmet } from "react-helmet";

export default function Inbox() {
  const { user, token, allUsers, fetchAllUsers } = useAuthContext();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const firstLoadRef = useRef(true);

  // Track which partner we're chatting with; null = no chat open
  const [openChatWith, setOpenChatWith] = useState(null);

  // Lock body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = openChatWith ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [openChatWith]);

  // Simplify for change detection
  const simplifyConversation = (conv) => ({
    id: conv._id,
    messagesCount: conv.messages?.length ?? 0,
    unreadCount:
      conv.messages?.filter(
        (m) => !m.read && m.sender !== user.user_id
      ).length ?? 0,
  });
  const prevSimplifiedRef = useRef([]);

  const conversationsEqual = (a, b) => {
    if (a.length !== b.length) return false;
    return a.every(
      (x, i) =>
        x.id === b[i].id &&
        x.messagesCount === b[i].messagesCount &&
        x.unreadCount === b[i].unreadCount
    );
  };

  // Fetch conversations (initially show loading; thereafter silent)
  const fetchConversations = useCallback(async () => {
    if (!user) return;
    if (firstLoadRef.current) setLoading(true);
    try {
      const { data: fetched } = await axios.get(
        `${conversationsURL}/${user.user_id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const simplified = fetched
        .sort((a, b) => a._id.localeCompare(b._id))
        .map(simplifyConversation);

      if (!conversationsEqual(prevSimplifiedRef.current, simplified)) {
        prevSimplifiedRef.current = simplified;
        setConversations(fetched);
      }
    } catch (err) {
      console.error("Error fetching conversations:", err);
    } finally {
      if (firstLoadRef.current) {
        setLoading(false);
        firstLoadRef.current = false;
      }
    }
  }, [user, token]);

  // Ensure users list is loaded
  useEffect(() => {
    if (!allUsers.length) fetchAllUsers();
  }, [allUsers, fetchAllUsers]);

  // Poll on mount and every 5s
  useEffect(() => {
    if (!user) return;
    fetchConversations();
    const iv = setInterval(fetchConversations, 5000);
    return () => clearInterval(iv);
  }, [user, fetchConversations]);

  const getPartner = (conv) => {
    const pid = conv.participants.find((id) => id !== user.user_id);
    return allUsers.find((u) => u.user_id === pid);
  };

  const formatDateOrTime = (iso) => {
    const d = new Date(iso);
    const today = new Date();
    if (d.toDateString() === today.toDateString()) {
      return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    }
    return d.toLocaleDateString([], {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  // Open chat modal
  const handleConversationClick = (conv) => {
    const partner = getPartner(conv);
    if (!partner) return;
    setOpenChatWith({
      senderId: user.user_id,
      recipientId: partner.user_id,
    });
  };

  // Close & refresh
  const handleCloseChat = () => {
    setOpenChatWith(null);
    // Immediately refresh to pick up read changes
    fetchConversations();
  };

  return (
    <>
      <Helmet>
        <title>Inbox – SomeOne4u</title>
        <meta
          name="description"
          content="Check and manage your conversations with other members on SomeOne4u."
        />
        <meta
          name="keywords"
          content="SomeOne4u, dating, inbox, messages, chat"
        />
        <link rel="canonical" href="https://someone4u.com/inbox" />

        <meta
          property="og:title"
          content="Inbox – SomeOne4u"
        />
        <meta
          property="og:description"
          content="View and reply to your messages from fellow members on SomeOne4u."
        />
        <meta
          property="og:url"
          content="https://someone4u.com/inbox"
        />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="SomeOne4u" />
      </Helmet>

    <div>
      {/* Inbox */}
      <div
        className={`min-h-screen bg-gray-100 p-6 ${
          openChatWith ? "pointer-events-none select-none" : ""
        }`}
      >
        <div className="max-w-4xl mx-auto bg-white shadow rounded-lg overflow-hidden">
          <div className="p-4 border-b">
            <h1 className="text-2xl font-bold text-red-400">Inbox</h1>
          </div>

          {loading ? (
            <div className="p-4">
            <div className="flex flex-col justify-center items-center h-64 space-y-4">
              <p className="text-gray-600">Loading conversations…</p>
              <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-red-400 border-solid"></div>
            </div>
          </div>          
          ) : conversations.length === 0 ? (
            <div className="p-4">
              <p className="text-gray-600">No conversations found.</p>
            </div>
          ) : (
            <ul>
              {conversations.map((conv) => {
                const partner = getPartner(conv);
                const unread = conv.messages?.filter(
                  (m) => !m.read && m.sender !== user.user_id
                ).length;
                const last =
                  conv.messages && conv.messages.length
                    ? conv.messages[conv.messages.length - 1]
                    : null;
                return (
                  <li
                    key={conv._id}
                    onClick={() => handleConversationClick(conv)}
                    className="cursor-pointer hover:bg-gray-50 p-4 border-b flex items-center justify-between"
                  >
                    <div className="flex items-center">
                      <img
                        src={
                          partner?.profile_image ||
                          "/image/operator.png"
                        }
                        alt={partner?.firstName || ""}
                        className="w-12 h-12 rounded-full object-cover mr-4"
                      />
                      <div>
                      <h2 className="text-lg font-semibold">
  {partner
    ? `${partner.firstName} ${partner.lastName}`
    : allUsers.length === 0
    ? "Loading..."
    : "Unknown"}
</h2>
                        {last && (
                          <p className="text-sm text-gray-600">
                            {last.text.length > 30
                              ? `${last.text.slice(0, 30)}…`
                              : last.text}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col items-end">
                      {last && (
                        <span className="text-xs text-gray-500">
                          {formatDateOrTime(last.createdAt)}
                        </span>
                      )}
                      {unread > 0 && (
                        <div className="mt-2 bg-red-500 text-white text-xs w-6 h-6 rounded-full flex items-center justify-center">
                          {unread}
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      {/* Chat Modal */}
      {openChatWith && (
        <div
          className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50"
          onClick={handleCloseChat}
        >
          <div
           className="bg-white w-full max-w-3xl mx-4 md:mx-auto h-[80vh] rounded-lg shadow-lg overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <ChatContent
              senderId={openChatWith.senderId}
              recipientId={openChatWith.recipientId}
              onClose={handleCloseChat}
            />
          </div>
        </div>
      )}
    </div>
    </>
  );
}
